/* eslint-disable @nx/enforce-module-boundaries */
import {
  Badge,
  Box,
  Button,
  HStack,
  Popover,
  PopoverArrow,
  PopoverContent,
  PopoverTrigger,
  Text,
  Tooltip,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import ChatSharpIcon from '@gamma/ui/icons/ChatSharpIcon';
import InfoSharpIcon from '@gamma/ui/icons/InfoSharpIcon';
import { PageHeader, PageHeaderProps } from '@gamma/ui/layout';
import { useContext, useMemo } from 'react';
import { DetectionAssignee } from '..';
import { DetectionStatus } from '..';
import {
  Detection,
  DetectionRank,
} from '@investigator/shared/generated/graphql';
import { StatusStrings } from '../../../entities/components';
import { SeverityScore } from '@investigator/shared/components';
import { i18n } from '../../../../shared/localization';
import { getSourceEntityForSuppression } from '@investigator/shared/utils';
import { useDetectionEntityStatusBanners } from '../../hooks';
import {
  AppConfigurationsContext,
  AuthContext,
  SlideDrawerContext,
} from '../../../../shared/providers';
import { IconButton } from '@gamma/ui/buttons';
import { Modal } from '@gamma/ui/overlay';
import { ActionsMenuButtonV2 } from '../DetectionActions/ActionsMenuButtonV2';
import { ActionsMenuButton } from '../DetectionActions';
import {
  DetectionNotesDrawer,
  IGetDetectionVariables,
} from '../DetectionDrawer';
import { AddNoteContent } from '../DetectionNotes';
import { EnableGPT } from './EnableGPT';
import { useDetectionContext } from '../../context';
import { DetectionQueryVariables } from '../../pages';

export interface IDetectionDetailsHeaderProps
  extends Pick<PageHeaderProps, 'breadcrumbs' | 'prevPage'> {
  getDetectionVariables?: IGetDetectionVariables;
  relatedEntitiesVariables?: DetectionQueryVariables;
  relatedDetectionsVariables?: DetectionQueryVariables;
  queryDetectionsPaginatedVariables?: DetectionQueryVariables;
  isCustomSeverityScore?: boolean;
  metadataLoading?: boolean;
}

const { detection: detectionText } = i18n.pages.detections;
const { notes, addNote } = detectionText;

function sanitizeDetection(
  detection: Detection | undefined,
): Detection | undefined {
  if (!detection) return undefined;
  const keys = detection.detection_keys ?? {
    tenant_detection: '',
    tenant_detection_entity: '',
    tenant_entity: '',
  };
  return {
    ...detection,
    detection_keys: keys,
  } as Detection;
}

export const DetectionDetailsHeader = ({
  getDetectionVariables,
  relatedEntitiesVariables,
  relatedDetectionsVariables,
  queryDetectionsPaginatedVariables,
  isCustomSeverityScore,
  metadataLoading,
  ...rest
}: IDetectionDetailsHeaderProps) => {
  const { appConfigurations } = useContext(AppConfigurationsContext);
  const isLLMSummary = appConfigurations?.feature_flags.llm_summary || false;
  const {
    userRole,
    isOrgTenant,
    userLicense,
    userPrivileges = {},
  } = useContext(AuthContext);
  const { detectionData } = useDetectionContext();
  const { tenant = '' } = userLicense ?? {};
  const hasPrivateDataEnabled = Boolean(
    userPrivileges?.[tenant ?? '']?.chatgpt_private_data,
  );
  const {
    isOpen: isAddNoteModalOpen,
    onOpen: addNoteModalOnOpen,
    onClose: addNoteModalOnClose,
  } = useDisclosure();
  const {
    setIsSlideDrawerOpen,
    setPortalType,
    setSlideDrawerBody,
    setSlideDrawerTitle,
    setSlideDrawerActions,
  } = useContext(SlideDrawerContext);

  const { rank } = useMemo(() => {
    const excludedEntityInfo = {
      rank: null as DetectionRank | null,
      latest_start_timestamp: null as number | null,
    };

    if (detectionData?.rank && detectionData?.latest_start_timestamp) {
      excludedEntityInfo.rank = detectionData?.rank;
      excludedEntityInfo.latest_start_timestamp =
        detectionData?.latest_start_timestamp;
    }

    return excludedEntityInfo;
  }, [detectionData?.rank, detectionData?.latest_start_timestamp]);

  const sanitizedDetectionData = sanitizeDetection(
    (detectionData as Detection) ?? undefined,
  );

  // Get source entity for suppression queries
  const sourceEntity = useMemo(
    () => getSourceEntityForSuppression(detectionData),
    [detectionData],
  );

  // Use shared hook to get status strings (suppression & escalation banners)
  const { statusStrings, excludedEntitiesData } =
    useDetectionEntityStatusBanners({
      detection: detectionData,
      sourceEntity,
    });

  // TODO: Delete this check and any negated logic
  const isADV2 =
    detectionData?.alert_info?.alert_type === 'anomaly' ||
    detectionData?.alert_info?.alert_type === 'suricata_corelight' ||
    detectionData?.alert_info?.alert_type === 'yara_corelight' ||
    detectionData?.alert_info?.alert_type === 'notice' ||
    detectionData?.alert_info?.alert_type === 'ml' ||
    detectionData?.alert_info?.alert_type === 'custom_search_rule';

  return isADV2 ? (
    <PageHeader {...rest} size="lg" variant="stacked-flushed">
      {statusStrings.length > 0 && (
        <Box paddingBottom="2" marginBottom="4">
          <StatusStrings statusStrings={statusStrings} />
        </Box>
      )}
      <HStack
        mt={-2.5}
        justifyContent="space-between"
        alignItems="start"
        data-testid="detection-details-header"
      >
        <HStack spacing={4} alignItems="center">
          <SeverityScore score={rank?.severity as number} circleSize="72px" />
          {detectionData?.detection_id &&
            detectionData?.alert_info?.alert_name && (
              <VStack alignItems={'start'}>
                <HStack
                  __css={{
                    wordWrap: 'anywhere',
                  }}
                  data-testid="detection-alert-name"
                  alignItems="start"
                >
                  <Text
                    fontSize="16px"
                    lineHeight="24px"
                    fontWeight={600}
                  >{`${detectionData?.alert_info?.alert_name} | ${detectionData?.alert_entity?.entity_name}`}</Text>
                  {!isOrgTenant && isLLMSummary && !hasPrivateDataEnabled && (
                    <Popover trigger="hover" placement="top">
                      <PopoverTrigger>
                        <InfoSharpIcon
                          color="text.secondary"
                          data-testid="highest-risk-detections-info-label"
                        />
                      </PopoverTrigger>
                      <PopoverContent w="350px">
                        <PopoverArrow />
                        <EnableGPT />
                      </PopoverContent>
                    </Popover>
                  )}
                </HStack>
                <HStack alignItems="space-between" lineHeight="24px">
                  {/* For components expecting local type, pass sanitizedDetectionData */}
                  <DetectionAssignee
                    refetchMain={true}
                    detectionInfo={sanitizedDetectionData}
                    assignmentInfo={
                      sanitizedDetectionData?.assignment_info ?? undefined
                    }
                    getDetectionVariables={getDetectionVariables}
                  />
                  <Box minWidth="160px">
                    <DetectionStatus
                      refetchMain={true}
                      detectionInfo={sanitizedDetectionData}
                      detectionStatus={sanitizedDetectionData?.detection_status}
                    />
                  </Box>
                </HStack>
              </VStack>
            )}
          {isOrgTenant && (
            <Badge variant="outline" data-testid="tenant-display-name-badge">
              {detectionData?.tenant_info?.tenant_display_name}
            </Badge>
          )}
        </HStack>
        <HStack>
          {/* For components expecting generated type, pass detectionData */}
          <ActionsMenuButtonV2
            getDetectionVariables={getDetectionVariables}
            userRole={userRole}
            queryDetectionsPaginatedVariables={
              queryDetectionsPaginatedVariables
            }
            metadataLoading={metadataLoading}
            refetchQueries={[]}
            excludedEntitiesData={excludedEntitiesData}
            excludedEntitiesLoading={false}
          />
        </HStack>
      </HStack>
    </PageHeader>
  ) : (
    <PageHeader {...rest} size="lg" variant="stacked-flushed">
      {statusStrings.length > 0 && (
        <Box paddingBottom="2" marginBottom="4">
          <StatusStrings statusStrings={statusStrings} />
        </Box>
      )}
      <HStack
        mt={-2.5}
        justifyContent="space-between"
        alignItems="start"
        data-testid="detection-details-header"
      >
        <HStack spacing={3} alignItems="center">
          <SeverityScore score={rank?.severity as number} />
          {detectionData?.detection_id &&
            detectionData?.alert_info?.alert_name && (
              <HStack
                __css={{
                  wordWrap: 'anywhere',
                }}
                data-testid="detection-alert-name"
              >
                <Text>{`${detectionData?.alert_info?.alert_name} | ${detectionData?.alert_entity?.entity_name}`}</Text>
                {!isOrgTenant && isLLMSummary && !hasPrivateDataEnabled && (
                  <Popover trigger="hover" placement="top">
                    <PopoverTrigger>
                      <InfoSharpIcon
                        color="text.secondary"
                        data-testid="highest-risk-detections-info-label"
                      />
                    </PopoverTrigger>
                    <PopoverContent w="350px">
                      <PopoverArrow />
                      <EnableGPT />
                    </PopoverContent>
                  </Popover>
                )}
              </HStack>
            )}
          {isOrgTenant && (
            <Badge variant="outline">
              {detectionData?.tenant_info?.tenant_display_name}
            </Badge>
          )}
        </HStack>
        <HStack>
          {/* For components expecting local type, pass sanitizedDetectionData */}
          <ActionsMenuButton
            getDetectionVariables={getDetectionVariables}
            userRole={userRole}
            refetchQueries={[]}
            excludedEntitiesData={excludedEntitiesData}
            excludedEntitiesLoading={false}
          />
          {!metadataLoading && (
            <Tooltip label={detectionText.notes} openDelay={500}>
              <IconButton
                size="sm"
                variant="solid"
                colorScheme="gray"
                aria-label="notes"
                onClick={() => {
                  setPortalType?.('notes');
                  setSlideDrawerTitle?.(
                    <>
                      <Box>{notes}</Box>
                      {userRole !== 'viewer' && (
                        <Button
                          variant="solid"
                          colorScheme="gray"
                          marginTop="1"
                          onClick={addNoteModalOnOpen}
                        >
                          {addNote}
                        </Button>
                      )}
                    </>,
                  );
                  setSlideDrawerBody?.(
                    <DetectionNotesDrawer item={sanitizedDetectionData} />,
                  );
                  setSlideDrawerActions?.(null);
                  setIsSlideDrawerOpen?.(true);
                }}
                data-testid="notes"
                icon={<ChatSharpIcon />}
              />
            </Tooltip>
          )}
        </HStack>
      </HStack>
      <Modal
        size="sm"
        isOpen={isAddNoteModalOpen}
        onClose={addNoteModalOnClose}
        title={addNote}
        body={
          <AddNoteContent onClose={addNoteModalOnClose} refetchMain={true} />
        }
      />
    </PageHeader>
  );
};
